/**
 * How a restaurant row from GET /restaurants is read for a card.
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE reading for every surface that draws a restaurant from the listing
 * payload: the /restaurants page (its cards and its board) and the app
 * showcase on the landing page. The payload already embeds
 * `sections[].products[]` with an image and a price on every dish, plus
 * `cardImageUrl`, `listingHero` and `ownerDetails`, so nothing here makes a
 * request.
 */

/* Vendor-entered strings, tidied only where they would otherwise read as a
   defect rather than as their data. A trailing full stop on a dish name and a
   SHOUTED cuisine are both real values in production. */
export const tidyName = (value) =>
  String(value || "")
    .trim()
    .replace(/[.,;]+$/, "");

export const tidyCuisine = (value) =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) =>
      part
        .split(/\s+/)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" "),
    )
    .join(" · ");

/* The dishes a card may show: enabled, and with a photograph. */
export const dishesOf = (location) =>
  (location?.sections || [])
    .flatMap((section) => section.products || [])
    .filter((product) => product && product.enabled !== false && product.image);

/**
 * The cover dish: which single photograph represents this restaurant on a
 * card.
 *
 * Order matters, and each step is a fallback for the one above failing:
 *   1. the dish the vendor or Lusuku staff pinned  (restaurant.listingHero)
 *   2. the first enabled dish that has a photo     (upload order, unchosen)
 *   3. nothing, and the card falls back to the logo on a warm plate
 *
 * Step 2 shipped first and step 1 exists because of what it exposed: with the
 * card leading on food, the most valuable image a restaurant owns was being
 * selected by whatever happened to be uploaded first. A pin that points at a
 * dish since disabled or deleted simply misses here and step 2 catches it, so
 * a stale pin degrades instead of emptying the card.
 */
export const coverDishOf = (location, dishes) => {
  const pinnedId = location?.listingHero?.productId
    ? String(location.listingHero.productId)
    : null;
  const pinned = pinnedId
    ? dishes.find((dish) => String(dish._id) === pinnedId)
    : null;
  return pinned || dishes[0] || null;
};

/* The card's picture. A card image the vendor uploaded for the LIST
   (Restaurant profile, "Card image", exact 1440x900) wins over the cover dish;
   `custom` then tells the caller to name the restaurant rather than a dish in
   the alt text. null means the card falls back to the restaurant's logo. */
export const restaurantHeroOf = (location, dishes = dishesOf(location)) =>
  typeof location?.cardImageUrl === "string" && location.cardImageUrl.trim()
    ? { image: location.cardImageUrl.trim(), name: location.name, custom: true }
    : coverDishOf(location, dishes);
